/**
 * Two-step OPAQUE handshake for account self-deletion.
 *
 * Wraps the `/auth/delete-account/start` + `/auth/delete-account/finalize`
 * endpoints behind a single `runDeleteAccountHandshake(password)` entry point
 * that returns a discriminated-union outcome — the calling UI branches on
 * `.kind` instead of parsing HTTP status codes or peeking at server error
 * shapes.
 *
 * Why a dedicated module (vs. inlining in SafeContext like `login`)?
 *   - The delete flow is unit-testable in isolation here (mock @serenity-kit
 *     + the API client, drive every result branch).
 *   - SafeContext stays focused on the unlock/lock state machine. The
 *     post-success "clear local state + navigate to sign-in" responsibility
 *     lives at the UI boundary (Task 6), not in this helper.
 *
 * The password is the user's master password; OPAQUE derives the proof
 * client-side and the server verifies it against the stored registration
 * record. The password never crosses the wire — the start request carries
 * an opaque blob, the finish request carries the proof.
 *
 * On `{ kind: 'success' }` the server has actually deleted the account by the
 * time the response returns. The caller MUST clear local tokens + vault state
 * and navigate to sign-in immediately — subsequent authenticated requests
 * with the now-orphaned JWT will 401 with no recovery path.
 *
 * That 401 is not a property of the JWT: it is still unexpired and still
 * verifies, because deleting the Cognito user cannot recall a token already
 * issued. It comes from the server-side deletion tombstone
 * (`server/lib/deletedAccount.ts`), which the request path checks. Worth
 * knowing if you are ever tempted to keep using the token for a farewell call —
 * every authenticated route refuses it.
 */

import * as opaque from '@serenity-kit/opaque';
import {
  deleteAccountStart,
  deleteAccountFinalize,
  type SoleAdminBlocker,
} from '../api/client';

export type { SoleAdminBlocker };

export type DeleteAccountResult =
  | { kind: 'success' }
  | { kind: 'wrong-password' }
  | { kind: 'sole-admin-block'; orgs: SoleAdminBlocker[] }
  | { kind: 'session-expired' }
  | { kind: 'network-error'; message: string };

/**
 * `opts.disbandOrgIds` opts in to deleting those families (from a prior
 * `sole-admin-block` result) along with the account. If the server's current
 * blocker list isn't fully covered it still returns `sole-admin-block` with the
 * fresh list.
 */
export async function runDeleteAccountHandshake(
  password: string,
  opts?: { disbandOrgIds?: string[] },
): Promise<DeleteAccountResult> {
  try {
    await opaque.ready;

    // ── Step 1: OPAQUE login start ───────────────────────────────────────────
    const { clientLoginState, startLoginRequest } = opaque.client.startLogin({ password });
    const startResp = await deleteAccountStart({ startLoginRequest });
    if (!startResp.ok) {
      // The start step's INVALID_CREDENTIALS means the server can't find an
      // OPAQUE record for the JWT's user — extremely unlikely (the user is
      // already signed in), but classify as wrong-password rather than
      // surfacing a confusing INVALID_CREDENTIALS toast.
      if (startResp.code === 'INVALID_CREDENTIALS') return { kind: 'wrong-password' };
      if (startResp.code === 'INVALID_SESSION' || startResp.code === 'UNAUTHORIZED'
          || startResp.code === 'NOT_AUTHENTICATED' || startResp.code === 'HTTP_401') {
        return { kind: 'session-expired' };
      }
      return { kind: 'network-error', message: startResp.error ?? 'Unknown error' };
    }

    // ── Step 2: OPAQUE proof on the client ───────────────────────────────────
    const loginResult = opaque.client.finishLogin({
      clientLoginState,
      loginResponse: startResp.data.loginResponse,
      password,
    });
    if (!loginResult) {
      // OPAQUE returned null — the server's blinded response doesn't match
      // what the client derived from `password`, i.e. wrong password.
      return { kind: 'wrong-password' };
    }

    // ── Step 3: finalize — server runs teardown if the proof verifies ────────
    const finishResp = await deleteAccountFinalize({
      sessionId: startResp.data.sessionId,
      finishLoginRequest: loginResult.finishLoginRequest,
      ...(opts?.disbandOrgIds?.length ? { disbandOrgIds: opts.disbandOrgIds } : {}),
    });
    if (finishResp.ok) return { kind: 'success' };

    if (finishResp.code === 'SOLE_ADMIN_BLOCK') {
      return { kind: 'sole-admin-block', orgs: finishResp.orgs ?? [] };
    }
    if (finishResp.code === 'INVALID_CREDENTIALS') return { kind: 'wrong-password' };
    if (finishResp.code === 'SESSION_EXPIRED' || finishResp.code === 'INVALID_SESSION'
        || finishResp.code === 'NOT_AUTHENTICATED' || finishResp.code === 'HTTP_401') {
      return { kind: 'session-expired' };
    }
    return { kind: 'network-error', message: finishResp.error ?? 'Unknown error' };
  } catch (e) {
    return { kind: 'network-error', message: e instanceof Error ? e.message : String(e) };
  }
}
